import {
  applySnapshot,
  cast,
  flow,
  getSnapshot,
  Instance,
  types,
} from 'mobx-state-tree';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import { formatDateToISO, killNulls } from 'utils';
import { Automation } from 'http-requests';
import { RequestStateModel } from './general-models';

const TaskModel = types
  .model('TaskModel', {
    task_id: types.string,
    meter: types.string,
    meter_name: types.string,
    serial_number: types.string,
    imsi: types.maybeNull(types.string),
    date: types.string,
    date_from: types.string,
    date_till: types.string,
    status: types.string,
    log_id: types.maybeNull(types.string),
    log: types.maybeNull(types.array(types.string)),
    status_history: types.array(
      types.model('history', {
        status: types.string,
        datetime: types.string,
      })
    ),
    warnings: types.maybeNull(types.array(types.string)),
    report_type: types.string,
    is_manual: types.maybeNull(types.boolean),
  })
  .volatile(() => ({
    isChecked: false as boolean,
    logStatus: RequestStateModel.create({}),
    cancelStatus: RequestStateModel.create({}),
  }))
  .views((self) => ({
    get logText() {
      if (!self.log || !self.log.length) {
        return 'Лог недоступен';
      }

      return self.log.join('\n');
    },
  }))
  .actions((self) => ({
    toggleCheck(check: boolean) {
      self.isChecked = check;
    },
    getTaskLog: flow(function* (id: string) {
      try {
        self.logStatus.loadingStates();
        const response: { log: string[] } = yield Automation.GetTaskLog(id);
        self.log = cast(response.log);
        self.logStatus.successStates();
        return response;
      } catch (e) {
        self.logStatus.errorStates();
        console.error(e);
      } finally {
        self.logStatus.finallyStates();
      }
    }),
    cancelTask: flow(function* (id: string) {
      try {
        self.cancelStatus.loadingStates();
        const response: { task_id: string } = yield Automation.CancelTask(id);

        if (response.task_id) {
          self.status = 'cancel';
        }

        self.cancelStatus.successStates();
        return response;
      } catch (e) {
        console.error(e);
        self.cancelStatus.errorStates();
      } finally {
        self.cancelStatus.finallyStates();
      }
    }),
  }));

export type ITaskModel = Instance<typeof TaskModel>;

const FiltersModel = types
  .model('FiltersModel', {
    limit: types.number,
    offset: types.number,
    houses: types.optional(types.array(types.string), []),
    meters: types.optional(types.array(types.string), []),
    imsi: types.maybeNull(types.string),
    date_from: types.maybeNull(types.Date),
    date_till: types.maybeNull(types.Date),
    statuses: types.optional(types.array(types.string), []),
    report_type: types.maybeNull(types.string),
    is_manual: types.maybeNull(types.boolean),
  })
  .views((self) => ({
    get snapshot() {
      return getSnapshot(self);
    },
    get count(): number {
      let count = 0;

      for (let key in this.snapshot) {
        !['limit', 'offset'].includes(key) &&
          this.snapshot[key as keyof typeof this.snapshot] != null &&
          count++;
      }

      return count;
    },
  }))
  .actions((self) => ({
    clearFilters() {
      applySnapshot(self, defaultFilters);
    },
    setFilter<T extends keyof typeof self.snapshot>(
      key: T,
      value: (typeof self)[T]
    ) {
      self[key] = value;
    },
  }));

const defaultFilters = {
  limit: 30,
  offset: 0,
  date_from: moment().subtract(7, 'day').toDate(),
  date_till: new Date(),
};

export type IFiltersModel = Instance<typeof FiltersModel>;

const Filters = types.snapshotProcessor(FiltersModel, {
  postProcessor: (self) => {
    return killNulls({
      ...self,
      houses: !!self.meters.length ? null : self.houses,
      date_from:
        (self.date_from &&
          formatDateToISO(moment(self.date_from).startOf('day'))) ||
        null,
      date_till:
        (self.date_till &&
          formatDateToISO(moment(self.date_till).endOf('day'))) ||
        null,
    });
  },
});

export const AutomationSuperStore = types
  .model('automationSuperStore', {
    statuses: types.optional(
      types.model('statuses', {
        tasks: types.optional(RequestStateModel, {}),
      }),
      {}
    ),
    totalCount: types.optional(types.number, 0),
    filters: types.optional(Filters, defaultFilters),
    tasks: types.optional(types.array(TaskModel), []),
  })
  .volatile(() => ({
    searchFilters: null as IFiltersModel | null,
  }))
  .views((self) => ({
    get checkedTasks() {
      return self.tasks.filter((task) => task.isChecked);
    },
    get isIdentityFilters(): boolean {
      return (
        self.searchFilters !== null &&
        isEqual(self.filters.snapshot, {
          ...self.searchFilters.snapshot,
          ...defaultFilters,
        })
      );
    },
  }))
  .actions((self) => ({
    clearTasks() {
      self.totalCount = 0;
      applySnapshot(self.tasks, []);
    },
    clearStore() {
      self.searchFilters = null;
      applySnapshot(self, {});
    },
    saveSearchFilters() {
      self.searchFilters = Filters.create(cloneDeep(self.filters));
    },
    getTasksList: flow(function* () {
      try {
        self.statuses.tasks.loadingStates();
        const response: { count_tasks: number; tasks: ITaskModel[] } =
          yield Automation.GetHeatHouseTasks(
            self.searchFilters?.snapshot || self.filters.snapshot
          );

        self.totalCount = response.count_tasks;
        self.tasks = cast(response.tasks);

        self.statuses.tasks.successStates();
        return response;
      } catch (e) {
        console.error(e);
        self.statuses.tasks.errorStates();
      } finally {
        self.statuses.tasks.finallyStates();
      }
    }),
  }));
